import {useMutation} from '@github-ui/react-query'
import {fetchCAPI, FetchCAPIError} from '../utils/fetch-capi'
import {repoTaskSessionsApiPath} from '../utils/api-url-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {IntentDetectionMode} from '../types/intent-detection-mode'
import {intentDetectionHeaders} from '../utils/intent-detection-headers'
import {extractImageUrls} from '../utils/markdown-images'
import {CopilotChatMessageStreamer} from '@github-ui/copilot-chat/utils/copilot-chat-message-streamer'
import type {StreamingChatMessage} from '../types/session'
import {useChatStreamContext, type StreamingState} from '../contexts/ChatStreamContext'

export interface CommandCreatedResponse {
  id: string
  state: string
  external_id?: string
  session_id?: string
}

export interface CreateAPISessionParams {
  nwo: string
  taskId: string
  problemStatement: string
  model?: string
  agentId?: number
  customAgent?: string
  eventType: string
  intentDetectionMode?: IntentDetectionMode
  setStreamingChatResponse?: (sessionId: string, response: string | null) => void
  setStreamingState?: (state: StreamingState) => void
  onCommandCreated?: (response: CommandCreatedResponse, problemStatement: string) => void
}

interface UseCreateAPISessionOptions {
  onError?: (error: Error) => void
  onSuccess?: () => void
  onCommandCreated?: (response: CommandCreatedResponse, problemStatement: string) => void
}

export function useCreateAPISession({onError, onSuccess, onCommandCreated}: UseCreateAPISessionOptions = {}) {
  const {setStreamingChatResponse, setStreamingState} = useChatStreamContext()
  const createSessionMutation = useMutation<void, Error, CreateAPISessionParams>({
    mutationKey: ['createAPISession'],
    mutationFn: async (params: CreateAPISessionParams): Promise<void> => {
      const imageCount = extractImageUrls(params.problemStatement).length

      sendEvent('copilot.agent_session.session_creation_requested', {
        nwo: params.nwo,
        task_id: params.taskId,
        image_count: imageCount,
      })

      try {
        await createSession({
          ...params,
          setStreamingChatResponse,
          setStreamingState,
          onCommandCreated,
        })
      } catch (error) {
        setStreamingState?.('closed')
        if (error instanceof FetchCAPIError) {
          sendEvent('copilot.agent_session.session_creation_failed', {
            nwo: params.nwo,
            task_id: params.taskId,
            status: error.status,
            error: error.message,
            image_count: imageCount,
          })
        }

        throw error
      }

      sendEvent('copilot.agent_session.session_creation_accepted', {
        nwo: params.nwo,
        task_id: params.taskId,
        image_count: imageCount,
      })
    },
    onError: error => {
      setStreamingState?.('closed')
      onError?.(error)
    },
    onSuccess: () => {
      setStreamingState?.('closed')
      onSuccess?.()
    },
  })

  const createAPISession = (params: CreateAPISessionParams) => {
    createSessionMutation.mutate(params)
  }

  return {
    createSession: createAPISession,
    isCreating: createSessionMutation.isPending,
    createError: createSessionMutation.error,
  }
}

/**
 * Helper function to create session via the Task API
 */
async function createSession({
  nwo,
  taskId,
  problemStatement,
  model,
  agentId,
  customAgent,
  eventType,
  intentDetectionMode,
  setStreamingChatResponse,
  setStreamingState,
  onCommandCreated,
}: CreateAPISessionParams): Promise<Response> {
  const endpoint = repoTaskSessionsApiPath(nwo, taskId)
  const additionalHeaders = intentDetectionHeaders(intentDetectionMode)

  if (setStreamingChatResponse) {
    setStreamingState?.('requested')
    const resp = await fetchCAPI({
      path: endpoint,
      method: 'POST',
      additionalHeaders,
      body: {
        problem_statement: problemStatement,
        event_type: eventType,
        ...(model && {model}),
        ...(agentId && {agent_id: agentId}),
        ...(customAgent && {custom_agent: customAgent}),
      },
    })

    const contentType = resp.headers.get('Content-Type') ?? ''

    // Handle command creation response (201 Created with JSON)
    // This occurs when intent detection routes a message to an active agent session
    if (resp.status === 201 && contentType.includes('application/json')) {
      const commandResponse = (await resp.json()) as CommandCreatedResponse
      onCommandCreated?.(commandResponse, problemStatement)
      setStreamingState?.('closed')
      return resp
    }

    if (contentType.includes('text/event-stream')) {
      const reader = resp.body?.getReader()
      if (!reader) {
        setStreamingState?.('closed')
        throw new Error('No reader found in response body')
      }

      const streamer = new CopilotChatMessageStreamer<StreamingChatMessage>(reader)
      let accumulated = ''
      let sessionID = ''
      for await (const message of streamer.stream()) {
        if (message.type === 'metadata') {
          sessionID = message.session_id
        }
        if (message.type === 'content') {
          const content = message.body
          if (content) {
            accumulated += content
            setStreamingChatResponse(sessionID, accumulated)
          }
        }
      }
    }

    setStreamingState?.('closed')

    return resp
  }

  return await fetchCAPI({
    path: endpoint,
    method: 'POST',
    additionalHeaders,
    body: {
      problem_statement: problemStatement,
      event_type: eventType,
      ...(model && {model}),
      ...(agentId && {agent_id: agentId}),
      ...(customAgent && {custom_agent: customAgent}),
    },
  })
}
